import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  MenuList,
  Popover,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@eatclubapp/ec-component-library';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { DateField } from '@mui/x-date-pickers/DateField';
import dayjs from 'dayjs';
import { setDateRangeAction, setDatesAction } from '../../actions/datePickerAction';
import { trackDetailedEvent } from '../../utils/analytics';
import { defaultDateRangeOptions, getDateRangeLabel } from '../../utils/insights/insightHelpers';
import { devLog, useQuery } from '../../utils';
import { formatDateRange } from '../../utils/dateHelpers';
import DownCaretSvg from '../../assets/icons/down_caret.svg';
import CalendarSvg from '../../assets/icons/calendar.svg';

const CustomDay = ({ day, range, endDateHover, setEndDateHover, ...rest }) => {
  const isSelected =
    (range.from && day.isSame(range.from, 'day')) || (range.to && day.isSame(range.to, 'day'));

  const isSelectingRange = range.from && !range.to;

  const isBetween = day.isBetween(range.from, range.to, 'day');
  const isBetweenHover = isSelectingRange && day.isBetween(range.from, endDateHover, 'day');

  return (
    <PickersDay
      {...rest}
      day={day}
      onMouseEnter={() => {
        if (isSelectingRange) {
          setEndDateHover(day);
        }
      }}
      onMouseLeave={() => {
        setEndDateHover(null);
      }}
      className={isSelected ? 'Mui-selected' : ''}
      style={{
        backgroundColor: isBetweenHover || isBetween ? 'rgba(0, 0, 0, 0.12)' : '',
        borderRadius: '8px',
      }}
    />
  );
};

const today = dayjs().startOf('day');
const minDate = dayjs('2020-01-01').startOf('day'); // todo: get this from API (restaurant start date)

export const DateRangePicker = ({
  /** Allowed date range options */
  options = [],
}) => {
  const query = useQuery();

  const muiTheme = useTheme();
  const breakpointSmDown = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  const [range, setRange] = useState({ from: null, to: null });
  const [endDateHover, setEndDateHover] = useState(null);

  const [value, setValue] = useState(null);

  const dates = useSelector((state) => state.dates);
  const dispatch = useDispatch();

  const [selected, setSelected] = useState(dates.value);

  const ref = useRef();

  const startDateParam = query.get('startDate');
  const endDateParam = query.get('endDate');

  // Automatically set the date if it's set in the URL
  useEffect(() => {
    if (startDateParam && endDateParam) {
      const newStartDate = dayjs(startDateParam, 'DD/MM/YYYY');
      const newEndDate = dayjs(endDateParam, 'DD/MM/YYYY');

      if (!newStartDate.isValid() || !newEndDate.isValid()) {
        devLog('invalid dates', 'invalid date strings', { startDateParam, endDateParam });
        return;
      }

      if (newStartDate.isBefore(minDate, 'day') || newEndDate.isAfter(today, 'day')) {
        devLog('invalid dates', 'invalid date range', { startDateParam, endDateParam });
        return;
      }

      if (newStartDate.isAfter(newEndDate, 'day')) {
        devLog('invalid dates', 'invalid ordering of dates', { startDateParam, endDateParam });
        return;
      }

      dispatch(setDatesAction(dayjs(newStartDate), dayjs(newEndDate)));
    }
  }, [startDateParam, endDateParam, dispatch]);

  const handleOpenCalendar = (el) => {
    setCalendarAnchorEl(el);
  };

  const handleCloseCalendar = () => {
    setCalendarAnchorEl(null);
  };

  useEffect(() => {
    if (calendarAnchorEl) {
      setRange({ from: dates.startDate, to: dates.endDate });
      setSelected(dates.value);
      setValue(dates.endDate);
    }
  }, [calendarAnchorEl, dates]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenDropdown = (el) => {
    setAnchorEl(el);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const allowedDateRangeOptions = defaultDateRangeOptions.filter((item) => {
    if (options.length > 0) {
      // 'custom' is a mandatory option
      return item.value === 'custom' || options.includes(item.value);
    }

    return true;
  });

  return (
    <>
      <ButtonGroup>
        <Button
          onClick={(e) => handleOpenDropdown(e.currentTarget)}
          style={{
            backgroundColor: '#FBFBFC',
            alignItems: 'center',
            borderRadius: '8px 0 0 8px',
            fontWeight: 400,
          }}
          variant='outlined'
          color='secondary'
          endIcon={<DownCaretSvg />}
        >
          {getDateRangeLabel(dates, allowedDateRangeOptions, 'Custom')}
        </Button>
        <Button
          ref={ref}
          onClick={() => handleOpenCalendar(ref.current)}
          style={{
            backgroundColor: '#FBFBFC',
            alignItems: 'center',
            borderRadius: '0 8px 8px 0',
            fontWeight: 400,
          }}
          variant='outlined'
          color='secondary'
          startIcon={<CalendarSvg />}
        >
          {formatDateRange(dates.startDate, dates.endDate)}
        </Button>
      </ButtonGroup>

      <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleCloseDropdown}>
        <MenuList>
          {allowedDateRangeOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => {
                if (option.value === 'custom') {
                  handleOpenCalendar(ref.current); // open the full calendar
                } else {
                  trackDetailedEvent('date_filter_changed', {
                    method: 'dropdown',
                    new: option.value,
                  });
                  dispatch(setDateRangeAction(option.value));
                }
                handleCloseDropdown();
              }}
              selected={option.value === dates.value}
              value={option.value}
            >
              {option.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <Popover
        anchorEl={calendarAnchorEl}
        keepMounted
        open={Boolean(calendarAnchorEl)}
        onClose={handleCloseCalendar}
        PaperProps={{
          style: {
            padding: 0,
            maxWidth: 'min-content',
          },
        }}
      >
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto auto',
          }}
        >
          {!breakpointSmDown && (
            <Box style={{ borderRight: '1px solid #F3F3F3', padding: '8px' }}>
              <MenuList>
                {allowedDateRangeOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => {
                      if (typeof option.dateRange === 'number') {
                        setRange({
                          from: today.subtract(option.dateRange, option.dateUnit),
                          to: today,
                        });
                      } else {
                        if (option.value === 'yesterday') {
                          setRange({
                            from: today.subtract(1, 'day'),
                            to: today.subtract(1, 'day'),
                          });
                        }

                        if (option.value === 'custom') {
                          // does nothing
                        }

                        if (option.value === 'all_time') {
                          setRange({
                            from: minDate,
                            to: today,
                          });
                        }
                      }

                      setSelected(option.value);
                      setValue(today);
                    }}
                    selected={option.value === selected}
                    value={option.value}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Box>
          )}

          <Box style={{ padding: '12px' }}>
            <Box style={{ display: 'flex', gap: '8px' }}>
              <DateField
                label='Start date'
                size='small'
                minDate={minDate}
                value={range.from}
                onChange={(newValue, context) => {
                  if (!context.validationError) {
                    if (range.to && newValue.isAfter(range.to)) {
                      // new start date is after the end date
                      setRange({ from: newValue, to: newValue });
                    } else {
                      setRange({ ...range, from: newValue });
                    }
                    setSelected('custom');
                    setValue(newValue);
                  }
                }}
                InputProps={{
                  style: { height: '36px', fontSize: '15px' },
                }}
                format='DD/MM/YYYY'
                disableFuture
              />
              <DateField
                label='End date'
                size='small'
                minDate={minDate}
                value={range.to}
                onChange={(newValue, context) => {
                  if (!context.validationError) {
                    if (range.from && newValue.isBefore(range.from)) {
                      // new end date is before the start date
                      setRange({ from: newValue, to: newValue });
                    } else {
                      setRange({ ...range, to: newValue });
                    }
                    setSelected('custom');
                    setValue(newValue);
                  }
                }}
                InputProps={{
                  style: { height: '36px', fontSize: '15px' },
                }}
                format='DD/MM/YYYY'
                disableFuture
              />
            </Box>

            <Box>
              <DateCalendar
                views={['day']}
                minDate={minDate}
                disableFuture
                fixedWeekNumber={6}
                onChange={(e) => {
                  if (range.to) {
                    setRange({ from: e, to: null });
                  } else {
                    if (e.isBefore(range.from, 'd')) {
                      setRange({ from: e, to: range.from });
                    } else {
                      setRange({ ...range, to: e });
                    }
                  }
                  setSelected('custom');
                  setValue(e);
                }}
                slots={{
                  day: CustomDay,
                }}
                slotProps={{
                  day: { range, endDateHover, setEndDateHover },
                }}
                value={value}
              />
            </Box>
          </Box>
        </Box>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
            borderTop: '1px solid #F3F3F3',
            width: '100%',
            padding: '8px',
          }}
        >
          <Button
            onClick={handleCloseCalendar}
            variant='outlined'
            color='secondary'
            style={{
              borderRadius: '8px',
              fontWeight: 400,
            }}
          >
            Cancel
          </Button>
          <Button
            color='secondary'
            disabled={!range}
            onClick={() => {
              if (selected === 'custom') {
                if (range.from || range.to) {
                  trackDetailedEvent('date_filter_changed', {
                    method: 'custom',
                    new_start: dayjs(range.from ?? range.to).format('YYYY-MM-DD'),
                    new_end: dayjs(range.to ?? range.from).format('YYYY-MM-DD'),
                  });
                  dispatch(
                    setDatesAction(
                      dayjs(range.from ?? range.to).format('YYYY-MM-DD'),
                      dayjs(range.to ?? range.from).format('YYYY-MM-DD'),
                    ),
                  );
                }
              } else {
                trackDetailedEvent('date_filter_changed', {
                  method: 'select',
                  new: selected,
                });
                dispatch(setDateRangeAction(selected));
              }

              handleCloseCalendar();
            }}
            style={{
              borderRadius: '8px',
              fontWeight: 400,
            }}
          >
            Confirm
          </Button>
        </div>
      </Popover>
    </>
  );
};
